import { useCallback, useMemo, useReducer, useRef } from 'react'
import {
  createShape,
  DEFAULT_SHAPE_SIZE,
  designBoundsForShapes,
  type DesignMode,
  type DesignShape,
  type DesignShapeType,
} from '../../types/design'
import { translateDesignShape } from './designGeometry'

/**
 * Tool identifiers. Includes shape-drawing tools (which double as shape types)
 * and non-shape action tools (ERASER / FILL_BUCKET / PAN / SELECT / CONNECTOR).
 */
export type DesignTool =
  | 'SELECT'
  | 'PAN'
  | 'CONNECTOR'
  | 'ERASER'
  | 'FILL_BUCKET'
  | DesignShapeType

export type Viewport = { zoom: number; x: number; y: number }

export type DesignEditorState = {
  shapes: DesignShape[]
  selection: string[]
  tool: DesignTool
  stickyTool: boolean
  viewport: Viewport
  showGrid: boolean
  snapToGrid: boolean
  snapToObjects: boolean
  clipboard: DesignShape[] | null
  /** Currently-picked palette colour — used by new stroke shape defaults and the fill bucket. */
  currentColor: string
}

type Action =
  | { type: 'SET_SHAPES'; shapes: DesignShape[] }
  | { type: 'ADD_SHAPES'; shapes: DesignShape[]; select?: boolean }
  | { type: 'UPDATE_SHAPES'; updates: Array<{ id: string; patch: Partial<DesignShape> }> }
  | { type: 'DELETE_SHAPES'; ids: string[] }
  | { type: 'REORDER'; ids: string[]; direction: 'FRONT' | 'BACK' | 'FORWARD' | 'BACKWARD' }
  | { type: 'SET_SELECTION'; ids: string[] }
  | { type: 'TOGGLE_SELECT'; id: string }
  | { type: 'SET_TOOL'; tool: DesignTool; sticky?: boolean }
  | { type: 'SET_VIEWPORT'; viewport: Viewport }
  | { type: 'SET_GRID'; show: boolean }
  | { type: 'SET_SNAP_GRID'; on: boolean }
  | { type: 'SET_SNAP_OBJECTS'; on: boolean }
  | { type: 'SET_CLIPBOARD'; shapes: DesignShape[] | null }
  | { type: 'SET_COLOR'; color: string }
  | { type: 'RESET'; shapes: DesignShape[] }

const initialState: DesignEditorState = {
  shapes: [],
  selection: [],
  tool: 'SELECT',
  stickyTool: true,
  viewport: { zoom: 1, x: 0, y: 0 },
  showGrid: true,
  snapToGrid: true,
  snapToObjects: true,
  clipboard: null,
  currentColor: '#38bdf8', // mosaic-keep: data (default shape color)
}

const reducer = (state: DesignEditorState, action: Action): DesignEditorState => {
  switch (action.type) {
    case 'SET_SHAPES':
      return { ...state, shapes: action.shapes }
    case 'ADD_SHAPES': {
      const next = [...state.shapes, ...action.shapes]
      return { ...state, shapes: next, selection: action.select === false ? state.selection : action.shapes.map((s) => s.id) }
    }
    case 'UPDATE_SHAPES': {
      const map = new Map(action.updates.map((u) => [u.id, u.patch]))
      return {
        ...state,
        shapes: state.shapes.map((s) => {
          const patch = map.get(s.id)
          if (!patch) return s
          if (s.locked && !('locked' in patch) && !('hidden' in patch)) return s
          return { ...s, ...patch } as DesignShape
        }),
      }
    }
    case 'DELETE_SHAPES': {
      const set = new Set(action.ids)
      const remaining = state.shapes.filter((s) => !set.has(s.id) || s.locked)
      const cleaned = remaining.map((s) => {
        if (s.type !== 'CONNECTOR') return s
        const next = { ...s }
        if (next.from.kind === 'SHAPE' && set.has(next.from.shapeId)) next.from = { kind: 'FREE', x: 0, y: 0 }
        if (next.to.kind === 'SHAPE' && set.has(next.to.shapeId)) next.to = { kind: 'FREE', x: 0, y: 0 }
        return next
      })
      return { ...state, shapes: cleaned, selection: state.selection.filter((id) => !set.has(id)) }
    }
    case 'REORDER': {
      const set = new Set(action.ids)
      const moving = state.shapes.filter((s) => set.has(s.id))
      const rest = state.shapes.filter((s) => !set.has(s.id))
      switch (action.direction) {
        case 'FRONT': return { ...state, shapes: [...rest, ...moving] }
        case 'BACK': return { ...state, shapes: [...moving, ...rest] }
        case 'FORWARD': {
          const arr = [...state.shapes]
          for (let i = arr.length - 2; i >= 0; i--) {
            if (set.has(arr[i].id) && !set.has(arr[i + 1].id)) {
              const tmp = arr[i]; arr[i] = arr[i + 1]; arr[i + 1] = tmp
            }
          }
          return { ...state, shapes: arr }
        }
        case 'BACKWARD': {
          const arr = [...state.shapes]
          for (let i = 1; i < arr.length; i++) {
            if (set.has(arr[i].id) && !set.has(arr[i - 1].id)) {
              const tmp = arr[i]; arr[i] = arr[i - 1]; arr[i - 1] = tmp
            }
          }
          return { ...state, shapes: arr }
        }
      }
      return state
    }
    case 'SET_SELECTION': return { ...state, selection: action.ids }
    case 'TOGGLE_SELECT':
      return {
        ...state,
        selection: state.selection.includes(action.id)
          ? state.selection.filter((id) => id !== action.id)
          : [...state.selection, action.id],
      }
    case 'SET_TOOL':
      return {
        ...state,
        tool: action.tool,
        stickyTool: action.tool !== 'SELECT' && action.tool !== 'PAN',
      }
    case 'SET_VIEWPORT': return { ...state, viewport: action.viewport }
    case 'SET_GRID': return { ...state, showGrid: action.show }
    case 'SET_SNAP_GRID': return { ...state, snapToGrid: action.on }
    case 'SET_SNAP_OBJECTS': return { ...state, snapToObjects: action.on }
    case 'SET_CLIPBOARD': return { ...state, clipboard: action.shapes }
    case 'SET_COLOR': return { ...state, currentColor: action.color }
    case 'RESET': return { ...initialState, shapes: action.shapes, viewport: state.viewport, currentColor: state.currentColor }
  }
}

export type DesignEditorApi = ReturnType<typeof useDesignEditor>

const HISTORY_LIMIT = 80

const isDesignShapeTypeTool = (tool: DesignTool): tool is DesignShapeType =>
  tool !== 'SELECT' && tool !== 'PAN' && tool !== 'CONNECTOR' && tool !== 'ERASER' && tool !== 'FILL_BUCKET'

export const useDesignEditor = (mode: DesignMode) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const past = useRef<DesignShape[][]>([])
  const future = useRef<DesignShape[][]>([])
  const snapshotting = useRef(false)

  const pushHistory = useCallback(() => {
    past.current.push(state.shapes)
    if (past.current.length > HISTORY_LIMIT) past.current.shift()
    future.current = []
  }, [state.shapes])

  const beginInteraction = useCallback(() => {
    past.current.push(state.shapes)
    if (past.current.length > HISTORY_LIMIT) past.current.shift()
    future.current = []
    snapshotting.current = true
  }, [state.shapes])

  const endInteraction = useCallback(() => {
    snapshotting.current = false
  }, [])

  const undo = useCallback(() => {
    const prev = past.current.pop()
    if (!prev) return
    future.current.push(state.shapes)
    dispatch({ type: 'SET_SHAPES', shapes: prev })
  }, [state.shapes])

  const redo = useCallback(() => {
    const next = future.current.pop()
    if (!next) return
    past.current.push(state.shapes)
    dispatch({ type: 'SET_SHAPES', shapes: next })
  }, [state.shapes])

  const reset = useCallback((shapes: DesignShape[]) => {
    past.current = []
    future.current = []
    dispatch({ type: 'RESET', shapes })
  }, [])

  const setTool = useCallback((tool: DesignTool) => {
    dispatch({ type: 'SET_TOOL', tool })
  }, [])

  const setCurrentColor = useCallback((color: string) => {
    dispatch({ type: 'SET_COLOR', color })
  }, [])

  const setSelection = useCallback((ids: string[]) => dispatch({ type: 'SET_SELECTION', ids }), [])
  const toggleSelect = useCallback((id: string) => dispatch({ type: 'TOGGLE_SELECT', id }), [])

  const setViewport = useCallback((vp: Viewport) => dispatch({ type: 'SET_VIEWPORT', viewport: vp }), [])
  const setShowGrid = useCallback((show: boolean) => dispatch({ type: 'SET_GRID', show }), [])
  const setSnapToGrid = useCallback((on: boolean) => dispatch({ type: 'SET_SNAP_GRID', on }), [])
  const setSnapToObjects = useCallback((on: boolean) => dispatch({ type: 'SET_SNAP_OBJECTS', on }), [])

  const addShape = useCallback((shape: DesignShape, opts?: { select?: boolean; history?: boolean }) => {
    if (opts?.history !== false) pushHistory()
    dispatch({ type: 'ADD_SHAPES', shapes: [shape], select: opts?.select !== false })
  }, [pushHistory])

  const addShapes = useCallback((shapes: DesignShape[]) => {
    pushHistory()
    dispatch({ type: 'ADD_SHAPES', shapes })
  }, [pushHistory])

  const updateShapes = useCallback((updates: Array<{ id: string; patch: Partial<DesignShape> }>) => {
    if (!snapshotting.current) pushHistory()
    dispatch({ type: 'UPDATE_SHAPES', updates })
  }, [pushHistory])

  const updateSingle = useCallback((id: string, patch: Partial<DesignShape>) => {
    updateShapes([{ id, patch }])
  }, [updateShapes])

  const deleteShapes = useCallback((ids: string[]) => {
    if (ids.length === 0) return
    pushHistory()
    dispatch({ type: 'DELETE_SHAPES', ids })
  }, [pushHistory])

  const reorder = useCallback((ids: string[], direction: 'FRONT' | 'BACK' | 'FORWARD' | 'BACKWARD') => {
    if (ids.length === 0) return
    pushHistory()
    dispatch({ type: 'REORDER', ids, direction })
  }, [pushHistory])

  const copy = useCallback(() => {
    if (state.selection.length === 0) return
    const selected = state.shapes.filter((s) => state.selection.includes(s.id))
    dispatch({ type: 'SET_CLIPBOARD', shapes: selected })
  }, [state.selection, state.shapes])

  const paste = useCallback(() => {
    if (!state.clipboard || state.clipboard.length === 0) return
    const newId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
    const cloned: DesignShape[] = state.clipboard.map((s) => ({
      ...s,
      id: newId(),
      ...translateDesignShape(s, 24, 24),
    } as DesignShape))
    pushHistory()
    dispatch({ type: 'ADD_SHAPES', shapes: cloned })
  }, [state.clipboard, pushHistory])

  const duplicate = useCallback(() => {
    if (state.selection.length === 0) return
    const newId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
    const dup: DesignShape[] = state.shapes
      .filter((s) => state.selection.includes(s.id))
      .map((s) => ({
        ...s,
        id: newId(),
        ...translateDesignShape(s, 24, 24),
      } as DesignShape))
    pushHistory()
    dispatch({ type: 'ADD_SHAPES', shapes: dup })
  }, [state.selection, state.shapes, pushHistory])

  const group = useCallback(() => {
    if (state.selection.length < 2) return
    const newId = () => `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
    const gid = newId()
    pushHistory()
    const updates: Array<{ id: string; patch: Partial<DesignShape> }> = state.selection.map((id) => ({ id, patch: { groupId: gid } as Partial<DesignShape> }))
    dispatch({ type: 'UPDATE_SHAPES', updates })
  }, [state.selection, pushHistory])

  const ungroup = useCallback(() => {
    if (state.selection.length === 0) return
    const targetGroups = new Set(state.shapes.filter((s) => state.selection.includes(s.id)).map((s) => s.groupId).filter(Boolean) as string[])
    if (targetGroups.size === 0) return
    pushHistory()
    const updates = state.shapes
      .filter((s) => s.groupId && targetGroups.has(s.groupId))
      .map((s) => ({ id: s.id, patch: { groupId: null } as Partial<DesignShape> }))
    dispatch({ type: 'UPDATE_SHAPES', updates })
  }, [state.selection, state.shapes, pushHistory])

  const selectionShapes = useMemo(() => state.shapes.filter((s) => state.selection.includes(s.id)), [state.shapes, state.selection])
  const selectionBounds = useMemo(() => designBoundsForShapes(selectionShapes), [selectionShapes])

  const createAndAddShape = useCallback((type: DesignShapeType, position: { x: number; y: number }, size?: { width: number; height: number }) => {
    const id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
    const s = createShape(type, position, size ?? DEFAULT_SHAPE_SIZE[type], id)
    addShape(s, { select: true })
    return s
  }, [addShape])

  return {
    mode,
    state,
    selectionShapes,
    selectionBounds,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
    setTool,
    setCurrentColor,
    setSelection,
    toggleSelect,
    setViewport,
    setShowGrid,
    setSnapToGrid,
    setSnapToObjects,
    addShape,
    addShapes,
    createAndAddShape,
    updateShapes,
    updateSingle,
    deleteShapes,
    reorder,
    copy,
    paste,
    duplicate,
    group,
    ungroup,
    undo,
    redo,
    reset,
    beginInteraction,
    endInteraction,
    isShapeTool: isDesignShapeTypeTool,
  }
}
